import { useState } from "react";
import { useToast } from "@/shared/rd";
import type { AppConfigView } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useUpdateAppConfigMutation } from "../services/useUpdateAppConfigMutation";
import { useResetAppConfigMutation } from "../services/useResetAppConfigMutation";
import { effectiveValue } from "../lib/configValue";

/** A failed write: why, and the same write again for "Try again". */
export type Failure = { message: string; retry: () => Promise<unknown> };

/**
 * The writes one key can take, with the board's feedback rules built in:
 *
 * - `save(value, { undo: true })` — a live (runtime) key: written at once, then
 *   a neutral toast with Undo. Undo is the same write with the previous value,
 *   or, for a key that had no stored value, removing the stored one again.
 * - `save(value, { undo: false })` — a start-up (boot) key: the caller has
 *   already confirmed; a success toast says it waits for a restart.
 * - `reset()` — reset to default / delete a custom key (confirmed by the caller).
 *
 * A failure is kept as `failure` for an error banner where the action happened,
 * whose Try again runs the same write and the same follow-up. Never a toast.
 */
export function useConfigWrites(config: AppConfigView, name: string) {
  const t = useTranslations();
  const toast = useToast();
  const update = useUpdateAppConfigMutation();
  const reset = useResetAppConfigMutation();
  const [failure, setFailure] = useState<Failure | null>(null);

  async function attempt(run: () => Promise<unknown>, onOk: () => void): Promise<boolean> {
    try {
      await run();
    } catch (err) {
      setFailure({ message: messageFor(err, t), retry: () => attempt(run, onOk) });
      return false;
    }
    setFailure(null);
    onOk();
    return true;
  }

  function save(value: string, { undo }: { undo: boolean }): Promise<boolean> {
    const previous = effectiveValue(config);
    const hadStoredValue = config.source === "db";
    const isBoolean = config.type === "boolean";
    if (!undo) {
      return attempt(
        () => update.mutateAsync({ key: config.key, value }),
        () => toast.show({ message: t("appConfigs.toastSavedRestart", { name }) }),
      );
    }
    const putBack = () =>
      void attempt(
        () => (hadStoredValue ? update.mutateAsync({ key: config.key, value: previous }) : reset.mutateAsync({ key: config.key })),
        () => toast.show({ tone: "neutral", message: t("appConfigs.toastUndone", { name }) }),
      );
    return attempt(
      () => update.mutateAsync({ key: config.key, value }),
      () =>
        toast.show({
          tone: "neutral",
          message: isBoolean
            ? t(value === "true" ? "appConfigs.toastTurnedOn" : "appConfigs.toastTurnedOff", { name })
            : t("appConfigs.toastSaved", { name }),
          actionLabel: t("appConfigs.undo"),
          onAction: putBack,
        }),
    );
  }

  function resetToDefault(): Promise<boolean> {
    const isCustom = config.origin === "custom";
    return attempt(
      () => reset.mutateAsync({ key: config.key }),
      () => toast.show({ message: t(isCustom ? "appConfigs.toastDeleted" : "appConfigs.toastReset", { name }) }),
    );
  }

  return {
    save,
    reset: resetToDefault,
    failure,
    clearFailure: () => setFailure(null),
    updatePending: update.isPending,
    resetPending: reset.isPending,
    isPending: update.isPending || reset.isPending,
  };
}

function messageFor(err: unknown, t: ReturnType<typeof useTranslations>): string {
  if (err instanceof ApiJsonClientError) {
    return err.message || t(keys.errors.generic);
  }
  return t(keys.errors.generic);
}
